/**
 * `ul_company_api_key` 는 key/sub_key1/sub_key2 라는 범용 3칸 구조라 컬럼 이름만으로는
 * 무엇을 넣어야 하는지 알 수 없다. 제공자별 의미를 여기서 한 번만 선언해
 * 관리 화면이 "CERTKEY", "담당자 ID" 같은 실제 라벨로 폼을 그리게 한다.
 *
 * 새 제공자를 붙일 때 백엔드에서 읽는 필드와 여기 라벨이 어긋나면 바로 장애가 되므로,
 * 소비 코드(ULS_API)를 확인하고 함께 갱신할 것.
 */

/** 값이 실제로 저장되는 컬럼. */
export type ApiKeySlot = "key" | "subKey1" | "subKey2";

export interface ApiKeyFieldSpec {
  slot: ApiKeySlot;
  label: string;
  required: boolean;
  placeholder?: string;
  hint?: string;
}

export interface ApiKeyProviderSpec {
  /** api_provider 컬럼 값. 서버가 대문자로 정규화한다. */
  provider: string;
  /** name 컬럼 값. (업체, name) 이 유니크 — 업체당 1건. */
  name: string;
  label: string;
  description: string;
  /**
   * 업체 키가 없을 때 소비 코드(ULS_API)의 동작.
   * - `shared`: 전사 공용 키(ul_api_key)로 폴백
   * - `none`: 폴백 없음 — 미등록이면 해당 기능이 실패한다
   * - `unknown`: 카탈로그 밖(직접 입력)이라 알 수 없음
   */
  fallback: "shared" | "none" | "unknown";
  fields: ApiKeyFieldSpec[];
}

export const API_KEY_PROVIDERS: ApiKeyProviderSpec[] = [
  {
    provider: "BAROBILL",
    name: "BAROBILL",
    label: "바로빌 (전자세금계산서)",
    description:
      "매입 세금계산서 조회에 사용. 업체 사업자번호가 바로빌 연동회원으로 등록되어 있어야 하고, 그 회원사에 아래 담당자 ID가 존재해야 한다.",
    fallback: "shared",
    fields: [
      {
        slot: "key",
        label: "연동 인증키 (CERTKEY)",
        required: true,
        placeholder: "바로빌에서 발급받은 CERTKEY",
      },
      {
        slot: "subKey1",
        label: "담당자 ID (UserID)",
        required: true,
        placeholder: "회원사 담당자 계정 ID",
        hint: "바로빌에서 UserID는 회원사(사업자번호)마다 따로 존재한다. 이 값이 해당 회원사에 없으면 조회가 0건으로 보인다.",
      },
      {
        slot: "subKey2",
        label: "비밀번호",
        required: false,
        placeholder: "선택 — 보관용",
        hint: "조회 API 파라미터에는 쓰이지 않는다. 회원 관리용으로만 보관.",
      },
    ],
  },
  {
    provider: "HOMETAX_BILL",
    name: "TAX_INVOICE_ISSUE",
    label: "홈택스빌 (매출 세금계산서 발급)",
    description:
      "매출 세금계산서 발급·상태조회에 사용. 공용 키로 폴백하지 않으므로 미등록 업체는 발급이 실패한다. 이미 발급한 건의 상태조회도 이 키로 하므로 계정을 바꿀 때 주의.",
    fallback: "none",
    fields: [
      {
        slot: "key",
        label: "API 키 (apikey)",
        required: true,
        placeholder: "홈택스빌에서 발급받은 API 키",
      },
      {
        slot: "subKey1",
        label: "회원 ID (hometaxbill_id)",
        required: true,
        placeholder: "홈택스빌 회원코드",
      },
      {
        slot: "subKey2",
        label: "비밀번호 (spass)",
        required: true,
      },
    ],
  },
  {
    provider: "ALIGO",
    name: "ALIGO",
    label: "알리고 (SMS)",
    description: "업체 명의로 SMS를 발송할 때 사용. 미등록 시 공용 키로 폴백한다.",
    fallback: "shared",
    fields: [
      { slot: "key", label: "API Key", required: true },
      { slot: "subKey1", label: "알리고 ID", required: true },
      {
        slot: "subKey2",
        label: "기본 발신번호",
        required: false,
        placeholder: "0212345678",
        hint: "발송 시 발신번호를 지정하지 않으면 이 값이 쓰인다.",
      },
    ],
  },
];

/** 카탈로그에 없는 제공자를 손으로 넣을 때 쓰는 폴백 스펙. */
export const CUSTOM_PROVIDER_SPEC: ApiKeyProviderSpec = {
  provider: "",
  name: "",
  label: "직접 입력",
  description:
    "카탈로그에 없는 제공자. 소비 코드가 읽는 컬럼을 확인하고 key / subKey1 / subKey2 에 맞춰 넣는다.",
  fallback: "unknown",
  fields: [
    { slot: "key", label: "key", required: true },
    { slot: "subKey1", label: "sub_key1", required: false },
    { slot: "subKey2", label: "sub_key2", required: false },
  ],
};

const SPEC_BY_NAME = new Map(API_KEY_PROVIDERS.map((p) => [p.name, p]));

/** 공용 폴백이 없어 업체 키를 반드시 등록해야 하는 제공자. */
export const REQUIRED_PROVIDERS = API_KEY_PROVIDERS.filter((p) => p.fallback === "none");

/** 저장된 행의 name 으로 스펙을 찾는다. 없으면 커스텀 스펙. */
export const findProviderSpec = (name: string | null | undefined): ApiKeyProviderSpec =>
  SPEC_BY_NAME.get((name ?? "").toUpperCase()) ?? CUSTOM_PROVIDER_SPEC;
